import { isPendingAnchor } from '_shared_/UI/AddOns/addOnAnchorFromSelection';
import { ADD_ON_DRAFT } from '_shared_/UI/AddOns/addOnModel';
import type { TourDefinition } from './tourRegistry';
import type { TourContext, TourScript, TourStep } from './tourRunner';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the Vuex store and the locale strings are plain JavaScript: they carry no types
type Untyped = any;

const dusk = (name: string) => `[dusk="${name}"]`;

const draftAnchor = (state: Untyped) => state.ui?.addOnsPanel?.editor?.draft?.anchor ?? null;
const editorOpen = (state: Untyped) => state.ui?.addOnsPanel?.editor != null;
const anchorIs = (state: Untyped, type: string, pending: boolean) => {
    const anchor = draftAnchor(state);
    return anchor?.type === type && isPendingAnchor(anchor) === pending;
};
const questionOpen = (state: Untyped) => Boolean(state.ui?.confirmationModal?.opened);

// Touch phones and the last page have no page button, and on a narrow window
// the panel's full-screen sheet lies over the one there is.
const pageButtonReachable = () => {
    const button = document.querySelector(dusk('NextPageButton'));
    if (!button) return false;
    const sheet = document.querySelector(dusk('ReaderAddOnsPanel'))?.getBoundingClientRect();
    if (!sheet?.width) return true;
    const { left, top, width, height } = button.getBoundingClientRect();
    const x = left + width / 2;
    const y = top + height / 2;
    return x < sheet.left || x > sheet.right || y < sheet.top || y > sheet.bottom;
};

type StepText = { title: string; text: string };

function step(id: string, text: StepText, rest: Omit<TourStep, 'id' | 'title' | 'text'> = {}): TourStep {
    return { id, title: text.title, text: text.text, ...rest };
}

// A capture asks for the page: the spotlight opens the page column and the
// popover parks over the dimmed panel beside it.
const onThePage = { element: dusk('ReaderViewport'), side: 'left', align: 'start' } as const;

// A skipped capture would leave the page waiting and the save disabled;
// cancelling gives the piece back the anchor it had.
const cancelCapture = ({ dispatch }: TourContext) => dispatch('cancelAddOnEditorAnchorCapture');

/**
 * The tour of the editor, on the book that is open. A point is captured
 * only where a page has fixed geometry and an image only where a zoom
 * exists (a reflowable EPUB), so the script names the kinds it cannot
 * show instead of asking for the impossible.
 */
function buildScript(getters: Untyped, strings: Untyped): TourScript {
    const s = strings.steps;
    const capturesPoints = Boolean(getters.canPlacePoint);
    const zoomsImages = Boolean(getters.isEpub) && !capturesPoints;

    const pointSteps: TourStep[] = capturesPoints
        ? [
              step('pickPoint', s.pickPoint, {
                  element: dusk('AddOnAnchorKind-point'),
                  advanceWhen: state => anchorIs(state, 'point', true),
              }),
              step('capturePoint', s.capturePoint, {
                  ...onThePage,
                  skipWhen: state => !anchorIs(state, 'point', true),
                  advanceWhen: state => anchorIs(state, 'point', false),
                  onSkip: cancelCapture,
              }),
          ]
        : [step('pointElsewhere', s.pointElsewhere)];

    const imageSteps: TourStep[] = zoomsImages
        ? [
              step('captureImage', s.captureImage, {
                  // The zoom the tap opens draws below driver's overlay and
                  // outside the page column, so the whole reader stays open.
                  element: dusk('Reader'),
                  advanceWhen: state => editorOpen(state) && anchorIs(state, 'image', false),
              }),
          ]
        : [step('imageElsewhere', s.imageElsewhere)];

    return {
        ownsEditorFrom: 'anchorBlock',
        steps: [
            // Next opens the piece; the spotlit button would open a second one.
            step('create', s.create, {
                element: dusk('AddOnsCreateButton'),
                disableInteraction: true,
                expectsEditor: false,
                enter: ({ state, dispatch }) => {
                    if (!state.ui.addOnsPanel.opened) dispatch('toggleAddOnsPanel');
                    dispatch('closeAddOnViewer');
                },
            }),
            step('anchorBlock', s.anchorBlock, {
                element: dusk('AddOnAnchorBlock'),
                disableInteraction: true,
                enter: ({ dispatch }) => dispatch('createAddOn'),
            }),
            step('pickText', s.pickText, {
                element: dusk('AddOnAnchorKind-text'),
                advanceWhen: state => anchorIs(state, 'text', true),
            }),
            // A skipped pick leaves nothing to capture, and a skipped capture
            // nothing to change.
            step('captureText', s.captureText, {
                ...onThePage,
                skipWhen: state => !anchorIs(state, 'text', true),
                advanceWhen: state => anchorIs(state, 'text', false),
                onSkip: cancelCapture,
            }),
            // The button asks the page again and unmounts itself: narrated only.
            step('recapture', s.recapture, {
                element: dusk('AddOnAnchorRecapture'),
                disableInteraction: true,
                skipWhen: state => !anchorIs(state, 'text', false),
            }),
            ...pointSteps,
            ...imageSteps,
            step('turnPage', s.turnPage, {
                element: dusk('NextPageButton'),
                // Where the button cannot be pressed the sheet also covers the
                // page a swipe would turn. With no turn the question never
                // opens, so the steps about it pass over too.
                skipWhen: () => !pageButtonReachable(),
                // A skipped capture leaves the anchor pending, and a pending
                // anchor follows the reader without asking; the question needs
                // a piece that is somewhere.
                enter: ({ state, dispatch }) => {
                    if (isPendingAnchor(draftAnchor(state))) dispatch('cancelAddOnEditorAnchorCapture');
                },
                advanceWhen: state => questionOpen(state),
            }),
            step('leaveQuestion', s.leaveQuestion, {
                // The dusk name sits on the full-viewport backdrop; the popover
                // belongs beside the dialog, clear of its Stay and Go buttons.
                element: `${dusk('ReaderModal')} [role="dialog"]`,
                advanceWhen: state => !questionOpen(state),
            }),
            // One step for both answers: while the anchor is pending (they went
            // with a passage) it asks for the capture; once it is settled (a
            // point, a page, an image) it reads as the confirmation and
            // advances at once.
            step('afterLeave', s.captureAgain, {
                ...onThePage,
                advanceWhen: state => !isPendingAnchor(draftAnchor(state)),
                onSkip: cancelCapture,
            }),
            step('preview', s.preview, { element: dusk('AddOnsPreviewButton'), disableInteraction: true }),
            step('draft', s.draft, {
                // The page badge list keeps a hidden copy of the pill.
                element: `${dusk('ReaderAddOnsPanel')} ${dusk('AddOnDraftPill')}`,
                enter: ({ commit }) => commit('setAddOnEditorField', { key: 'status', value: ADD_ON_DRAFT }),
            }),
            step('publishing', s.publishing, { element: dusk('AddOnsSaveDraftButton'), disableInteraction: true }),
            step('finish', s.finish, { element: dusk('AddOnsCancelButton'), disableInteraction: true }),
        ],
        teardown: ({ dispatch }: TourContext) => {
            dispatch('cancelAddOnEditor');
        },
    };
}

export const ADD_ONS_EDITOR_TOUR: TourDefinition = {
    id: 'addons-editor',
    version: 1,
    stringsKey: 'addOnsEditor',
    isAvailable: getters => Boolean(getters.canEditAddOns),
    // Only over the list: the tour closes the piece the panel shows, and an
    // open form may hold an operator's unsaved work.
    offerWhen: (state, getters) =>
        Boolean(state.ui?.addOnsPanel?.opened) &&
        state.ui.addOnsPanel.addOnId == null &&
        !editorOpen(state) &&
        Boolean(getters.canEditAddOns),
    script: buildScript,
};
